import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useTranslation } from 'react-i18next';
import '../../style/EditAccountPage.css';

const EditAccountPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const { dispatch } = useAuthContext();
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState({ name: '', email: '', language: 'en', phoneNumber: '' });
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error(t('error.noAuthToken'));
                }

                const response = await axios.get(`${baseUrl}/api/users/account/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setUser(response.data);
            } catch (error) {
                console.log(error);
                enqueueSnackbar(t('error.fetchUserData'), { variant: 'error' });
            }
        };

        fetchUser();
    }, [id, enqueueSnackbar, baseUrl, t]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUser({ ...user, [name]: value });
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('error.noAuthToken'));
            }

            // Send only phoneNumber in E.164 format
            const userToSend = { ...user };
            const response = await axios.put(`${baseUrl}/api/users/account/${id}`, userToSend, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setLoading(false);
            enqueueSnackbar(t('editAccountPage.userUpdated'), { variant: 'success' });
            dispatch({ type: 'UPDATE_USER', payload: response.data });
            localStorage.setItem('user', JSON.stringify(response.data));

            navigate(`/account/${id}`);
        } catch (error) {
            setLoading(false);
            console.error(t('error.updateUser'), error.response || error.message);
            enqueueSnackbar(t('error.updateUser'), { variant: 'error' });
        }
    };

    return (
        <div className="edit-account-page bg-dafauna-background rounded-xl shadow-md p-8">
            <h2 className="edit-account-title text-dafauna-darkText text-2xl font-bold text-center mb-4">
                {t('accountPage.editTitle')}
            </h2>
            <form onSubmit={handleUpdateUser} className="edit-account-form space-y-4">
                <div className="form-group">
                    <label htmlFor="name" className="block text-dafauna-darkText font-medium">
                        {t('accountPage.name')}
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={user.name}
                        onChange={handleInputChange}
                        required
                        className="form-input w-full border border-gray-300 rounded-md px-4 py-2 capitalize focus:outline-none focus:border-dafauna-primary"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="email" className="block text-dafauna-darkText font-medium">
                        {t('accountPage.email')}
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={user.email}
                        onChange={handleInputChange}
                        required
                        className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="phone" className="block text-dafauna-darkText font-medium">
                        {t('accountPage.phoneNumber')}
                    </label>
                    <PhoneInput
                        country={'us'}
                        value={user.phoneNumber}
                        onChange={(value) => {
                            setUser({
                                ...user,
                                phoneNumber: value,
                            });
                        }}
                        inputProps={{
                            name: 'phone',
                            required: true,
                            autoFocus: false,
                        }}
                        enableSearch
                        placeholder={t('accountPage.phoneNumber')}
                        containerClass="w-full"
                        inputClass="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                        buttonClass="border border-gray-300 rounded-l-md"
                        dropdownClass="react-phone-dropdown"
                    />
                </div>
                <button
                    type="submit"
                    disabled={loading}
                    className="btn update-account-btn w-full bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                    {loading ? t('general.updating') : t('general.update')}
                </button>
            </form>
        </div>
    );
};

export default EditAccountPage;